import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as contactActions from "@rentiohq/shared-frontend/dist/redux/contact/contact.actions";
import * as contactSelectors from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import * as templateHooks from "@rentiohq/shared-frontend/dist/redux/template/template.hooks";
import * as documentPackageActions from "@rentiohq/shared-frontend/dist/reduxV2/documentPackage/documentPackage.actions";
import * as documentPackageSelectors from "@rentiohq/shared-frontend/dist/reduxV2/documentPackage/documentPackage.selectors";
import * as documentHooks from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.hooks";
import * as templateDocumentHooks from "@rentiohq/shared-frontend/dist/reduxV2/templateDocuments/templateDocument.hooks";
import { IMember } from "@rentiohq/shared-frontend/dist/types/auth.types";
import {
  EDocumentMemberTypes,
  IDocument,
} from "@rentiohq/shared-frontend/dist/types/document.types";
import {
  EPropertyMemberTypes,
  IProperty,
  TPropertyMember,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { ITask } from "@rentiohq/shared-frontend/dist/types/task.types";
import { differenceInDays } from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { getMembersWithRole } from "@rentiohq/shared-frontend/dist/utils/roles.utils";
import { appendQueryParams } from "@rentiohq/shared-frontend/dist/utils/url.utils";
import utils from "@rentiohq/web-shared/dist/utils";
import { EAssets } from "@rentiohq/web-shared-next/dist/assets";
import {
  Box,
  Button,
  ETagVariant,
  IMenuAction,
  SimpleCard,
  SpinningLoader,
  Stack,
  Tag,
  Typography,
  Link,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { Select as MuiSelect } from "@rentiohq/web-shared-next/dist/ui-components/Select";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import { IFormikValues } from "@rentiohq/web-shared-next/dist/utils/types/form";
import { flatten, isEmpty, join, uniqBy } from "lodash";
import queryString from "query-string";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import { ts } from "../../../../../../../services";
import { AccountList } from "../../../../../AccountList";
import { AddCustomMessage } from "../../../../../Modals/AddCustomMessage";
import { SignerList } from "../../../../../SignerList";
import { DocumentInfoStyles } from "./DocumentInfo.styles";

interface IProps {
  document: IDocument;
  linkedProperty?: IProperty;
  linkedTask?: ITask;
  mainActions: IMenuAction[];
}

export const DocumentInfo: React.FC<IProps> = props => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let location = useLocation();

  const { user } = authHooks.useSelf();

  const { document, linkedProperty, mainActions } = props;

  const { documentPackageId } = document;

  const [showShare, setShowShare] = React.useState(false);
  const [showCustomMessageModal, setShowCustomMessageModal] =
    React.useState(false);

  const [shareMember, setShareMember] = React.useState<number | undefined>(
    undefined,
  );

  const { updateMembers, error, isUpdating } = documentHooks.useUpdate({
    id: document.id,
  });

  const documentPackage = useSelector((state: IRootStore) =>
    documentPackageId
      ? documentPackageSelectors.getDetail(state, documentPackageId)
      : undefined,
  );
  const isFetchingDocumentPackage = useSelector((state: IRootStore) =>
    documentPackageId
      ? documentPackageSelectors.isFetchingDetail(state, documentPackageId)
      : false,
  );

  const { detail: templateDocument } = templateDocumentHooks.useDetail({
    shouldRefetch: false,
    id: document.templateDocumentId,
  });

  const { sentForReviewToAccountId, templateId } = templateDocument || {};

  const { detail: template } = templateHooks.useDetail({
    shouldRefetch: false,
    id: templateId,
  });

  const contact = useSelector((state: IRootStore) =>
    sentForReviewToAccountId
      ? contactSelectors.getContact(state, undefined, sentForReviewToAccountId)
      : undefined,
  );

  // Lifecycle
  React.useEffect(() => {
    if (!documentPackageId) {
      return;
    }

    if (isFetchingDocumentPackage) {
      return;
    }

    dispatch(
      documentPackageActions.getDetailStart.getAction({
        id: documentPackageId,
      }),
    );
  }, [documentPackageId]);

  React.useEffect(() => {
    if (!sentForReviewToAccountId) {
      return;
    }
    dispatch(
      contactActions.getContactsByAccountIds.actions.start({
        accountIds: [sentForReviewToAccountId],
      }),
    );
  }, [templateDocument]);

  React.useEffect(() => {
    if (error) {
      enqueueSnackbar(error?.message, {
        variant: "error",
      });
    }
  }, [error]);

  // Event handlers
  const handleShareWithMember = (customBody: string) => {
    if (!shareMember) {
      return;
    }

    updateMembers({
      membersToAdd: [
        {
          roles: [EDocumentMemberTypes.Viewer],
          accountId: shareMember,
        },
      ],
      membersToRemove: [],
      additionalParams: {
        customBody,
      },
    });
  };

  const handleRemoveMember = (accountIds: number[]) => {
    updateMembers({
      membersToAdd: [],
      membersToRemove: accountIds.map(accountId => ({
        roles: [EDocumentMemberTypes.Viewer],
        accountId,
      })),
    });
  };

  const filterMembersElseTaskOrSignerOnly = (member: TPropertyMember) => {
    if (member.roles.length === 1) {
      return ![
        EPropertyMemberTypes.TaskMember,
        EPropertyMemberTypes.Signer,
      ].includes(member.roles[0]);
    }

    return true;
  };

  const filterIfContractEnded60DaysAgo = (member: TPropertyMember) => {
    if (member?.contractInfo?.to) {
      const daysAgo = differenceInDays(new Date(), member.contractInfo.to);
      const isOverSixtyDays = daysAgo >= 60;
      return !isOverSixtyDays;
    }
    return true;
  };

  const filterUnassignedMembers = (member: TPropertyMember) => {
    return !document.members
      .map(member => member.account.id)
      .includes(member.account.id);
  };

  // Helpers
  const documentTypeData = utils.document.getTypeData(document.mimeType);
  const isPdf = documentTypeData.type.toLowerCase() === "pdf";

  const isAdmin = document.roles.includes(EDocumentMemberTypes.Admin);

  const adminMembers = getMembersWithRole(
    document.members,
    EDocumentMemberTypes.Admin,
  );

  const sharedWithMembers = getMembersWithRole(
    document.members,
    EDocumentMemberTypes.Viewer,
  );

  const parentMembers = uniqBy(
    flatten(linkedProperty?.members || []),
    "account.id",
  );

  const parentMembersAndNotYetMemberOfDocument = parentMembers
    .filter(filterMembersElseTaskOrSignerOnly)
    .filter(filterIfContractEnded60DaysAgo)
    .filter(filterUnassignedMembers)
    .filter(member => member.account.userId !== user!.id);

  const canShare = isAdmin && parentMembersAndNotYetMemberOfDocument.length > 0;

  const renderSigningContent = () => {
    if (documentPackageId && !documentPackage) {
      return <SpinningLoader />;
    }

    if (document.templateDocumentId && !templateDocument) {
      return <SpinningLoader />;
    }

    if (!documentPackage) {
      return (
        <Link
          label={getLocalizedText("document_package.excerpt.action")}
          underlined={false}
          onClick={() => {
            navigate(
              appendQueryParams({
                path: "/forms/document-package",
                queryParams: {
                  propertyId: document.propertyId,
                  contractId: templateDocument?.contractId || undefined,
                  documentIdToSign: document.id,
                },
              }),
            );
          }}
        />
      );
    }

    return <SignerList documentPackage={documentPackage} />;
  };

  const renderSendForEditAndSign = () => {
    if (document.templateDocumentId && !templateDocument) {
      return <SpinningLoader />;
    }

    if (!contact || !templateDocument?.sentForReviewAt) {
      return <SpinningLoader />;
    }
    const { account } = contact;

    return (
      <Tag variant={ETagVariant.Warning}>
        {getLocalizedText("document_package.edit.excerpt.action", {
          tenant: getName(account),
        })}
      </Tag>
    );
  };

  const renderSelectMember = (params: { data: IMember<string> }) => {
    const member = params.data;
    const { roles = [] } = member;

    return (
      <Stack flexDirection="column">
        <Box>{getName(member.account)}</Box>
        <Box>
          <Typography sx={sx.content.memberRoles}>
            {join(
              roles.map(role => ts.role(role)),
              ", ",
            )}
          </Typography>
        </Box>
      </Stack>
    );
  };

  const handleSubmitCustomMessage = (values: IFormikValues) => {
    const { customBody } = values;

    const customBodyClean = !isEmpty(customBody) ? customBody : undefined;
    if (!!customBodyClean) {
      handleShareWithMember(customBodyClean);
    }

    setShowCustomMessageModal(false);
  };

  const sx = DocumentInfoStyles();

  return (
    <>
      <Stack direction="column" gap={1}>
        {document.url && (
          <SimpleCard
            title={getLocalizedText("document.preview.header")}
            actions={mainActions}
          >
            {document.thumbnailUrl ? (
              <Box
                sx={sx.content.docPreview}
                onClick={() => utils.document.viewDocument(document)}
              >
                <img
                  style={sx.content.imgPreview}
                  src={document.thumbnailUrl}
                  alt={document.filename}
                />
              </Box>
            ) : (
              <Button
                variant={EButtonVariants.Text}
                onClick={() => utils.document.viewDocument(document)}
              >
                {getLocalizedText("document.view")}
              </Button>
            )}
          </SimpleCard>
        )}

        {isPdf &&
          document.url &&
          (!templateDocument?.sentForReviewAt || documentPackageId) && (
            <SimpleCard
              title={getLocalizedText("document_package.excerpt.heading")}
              actions={
                documentPackageId
                  ? [
                      {
                        content: getLocalizedText(
                          "document_package.view.action",
                        ),
                        onClick: () => {
                          navigate(
                            appendQueryParams({
                              path: location.pathname,
                              queryParams: {
                                ...queryString.parse(location.search),
                                documentPackageId,
                              },
                            }),
                          );
                        },
                      },
                    ]
                  : undefined
              }
            >
              {renderSigningContent()}
            </SimpleCard>
          )}

        {template?.tenantCanEdit && (
          <SimpleCard
            title={getLocalizedText("document_package.edit.excerpt.heading")}
          >
            {renderSendForEditAndSign()}
          </SimpleCard>
        )}

        {adminMembers.length > 0 && (
          <SimpleCard title={getLocalizedText("document.owner")}>
            <AccountList
              accounts={adminMembers.map(member => member.account)}
              getActions={() => []}
            />
          </SimpleCard>
        )}

        {canShare && (
          <SimpleCard
            title={getLocalizedText("documents.members.header.title")}
          >
            {isUpdating && sharedWithMembers.length === 0 && <SpinningLoader />}
            {sharedWithMembers.length > 0 && (
              <AccountList
                accounts={sharedWithMembers.map(member => member.account)}
                isLoading={isUpdating}
                getActions={({ accountIds }) => [
                  {
                    label: getLocalizedText("system.remove"),
                    icon: EAssets.DeleteIcon,
                    onClick: () => handleRemoveMember(accountIds),
                  },
                ]}
              />
            )}

            {showShare && (
              <>
                <Box mb={1}>
                  <MuiSelect
                    id={"document-contact-select"}
                    name={"document-contact-select"}
                    onChange={(value: number) => {
                      setShareMember(value);
                      setShowShare(false);
                      setShowCustomMessageModal(true);
                    }}
                    label={getLocalizedText("contact_select.search.label")}
                    // icon={<Icon source="search" />}
                    placeholder={ts.system("search")}
                    // @ts-ignore
                    options={parentMembersAndNotYetMemberOfDocument.map(
                      member => ({
                        label: renderSelectMember({ data: member }),
                        value: member.account.id,
                      }),
                    )}
                  />
                </Box>

                <Link
                  label={getLocalizedText("cancel")}
                  underlined={false}
                  onClick={() => setShowShare(false)}
                />
              </>
            )}

            {!showShare &&
              parentMembersAndNotYetMemberOfDocument.length > 0 && (
                <Box mt={1}>
                  <Link
                    label={getLocalizedText("property.documents.share.label")}
                    underlined={false}
                    onClick={() => setShowShare(true)}
                  />
                </Box>
              )}
          </SimpleCard>
        )}
      </Stack>

      {showCustomMessageModal && (
        <AddCustomMessage
          property={linkedProperty}
          showDialog={showCustomMessageModal}
          handleClose={() => setShowCustomMessageModal(false)}
          handleSubmit={handleSubmitCustomMessage}
        />
      )}
    </>
  );
};
